import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Clock, LogIn, LogOut, CheckCircle, Calendar, ShieldCheck } from "lucide-react";
import { useAuth } from "@/_core/hooks/useAuth";

function fmtTime(ts: number | null | undefined) {
  if (!ts) return "—";
  return new Date(ts).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function fmtMinutes(min: number) {
  if (min <= 0) return "0min";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h${m > 0 ? m + "min" : ""}` : `${m}min`;
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

type ClockLocation = {
  latitude?: number;
  longitude?: number;
  accuracyMeters?: number;
  status: "captured" | "denied" | "unavailable" | "unsupported";
};

function captureClockLocation(): Promise<ClockLocation> {
  if (!("geolocation" in navigator)) return Promise.resolve({ status: "unsupported" });

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracyMeters: position.coords.accuracy,
        status: "captured",
      }),
      (error) => resolve({ status: error.code === error.PERMISSION_DENIED ? "denied" : "unavailable" }),
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 0 }
    );
  });
}

export default function StaffClockIn() {
  const { user } = useAuth();
  const [now, setNow] = useState(new Date());
  const [isCollectingLocation, setIsCollectingLocation] = useState(false);
  const [registrationFeedback, setRegistrationFeedback] = useState<{
    type: "entrada" | "saída";
    locationStatus: ClockLocation["status"];
  } | null>(null);
  const utils = trpc.useUtils();

  // Atualizar relógio a cada segundo
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!registrationFeedback) return;
    const timeout = window.setTimeout(() => setRegistrationFeedback(null), 7000);
    return () => window.clearTimeout(timeout);
  }, [registrationFeedback]);

  const today = todayStr();

  // Buscar registro de ponto de hoje
  const { data: todayRecord, isLoading } = trpc.attendance.staffTodayRecord.useQuery(
    { workDate: today },
    { refetchInterval: 30000 }
  );

  const clockInMut = trpc.attendance.staffClockIn.useMutation({
    onSuccess: () => {
      utils.attendance.staffTodayRecord.invalidate();
      toast.success("Entrada registrada com sucesso!");
    },
    onError: (e) => toast.error(e.message),
  });

  const clockOutMut = trpc.attendance.staffClockOut.useMutation({
    onSuccess: () => {
      utils.attendance.staffTodayRecord.invalidate();
      toast.success("Saída registrada com sucesso!");
    },
    onError: (e) => toast.error(e.message),
  });

  const hasClockIn = todayRecord && (todayRecord as any).clockIn;
  const hasClockOut = todayRecord && (todayRecord as any).clockOut;

  const workedMinutes = hasClockIn && hasClockOut
    ? Math.round(((todayRecord as any).clockOut - (todayRecord as any).clockIn) / 60000)
    : hasClockIn
    ? Math.round((now.getTime() - (todayRecord as any).clockIn) / 60000)
    : 0;

  function getStatus() {
    if (!hasClockIn) return { label: "Não iniciado", color: "text-muted-foreground", bg: "bg-muted/50" };
    if (hasClockIn && !hasClockOut) return { label: "Em serviço", color: "text-green-600", bg: "bg-green-50" };
    return { label: "Jornada encerrada", color: "text-blue-600", bg: "bg-blue-50" };
  }

  const status = getStatus();

  async function registerClock(type: "entrada" | "saída") {
    setIsCollectingLocation(true);
    const location = await captureClockLocation();
    setIsCollectingLocation(false);
    const onSuccess = () => setRegistrationFeedback({ type, locationStatus: location.status });

    if (type === "entrada") clockInMut.mutate({ location }, { onSuccess });
    else clockOutMut.mutate({ location }, { onSuccess });
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-lg mx-auto space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Meu Ponto</h1>
          <p className="text-muted-foreground text-sm">{new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</p>
        </div>

        {/* Relógio */}
        <Card>
          <CardContent className="py-8 text-center">
            <div className="text-6xl font-mono font-bold tracking-tight">
              {now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </div>
            <div className={`mt-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium ${status.bg} ${status.color}`}>
              <div className={`w-2 h-2 rounded-full ${hasClockIn && !hasClockOut ? "bg-green-500 animate-pulse" : "bg-current opacity-50"}`} />
              {status.label}
            </div>
          </CardContent>
        </Card>

        {registrationFeedback && (
          <div role="status" className="animate-in fade-in zoom-in-95 duration-300 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-center shadow-sm">
            <div className="mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-emerald-500 text-white animate-bounce">
              <CheckCircle className="h-6 w-6" />
            </div>
            <p className="font-semibold text-emerald-800">Ponto de {registrationFeedback.type} registrado!</p>
            <p className="mt-1 text-xs text-emerald-700">
              {registrationFeedback.locationStatus === "captured"
                ? "Horário e localização do momento foram registrados."
                : "Horário registrado. A localização não ficou disponível neste dispositivo."}
            </p>
          </div>
        )}

        {/* Registro do dia */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Registro de Hoje
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoading ? (
              <div className="text-center text-muted-foreground py-4">Carregando...</div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 rounded-lg bg-green-50 border border-green-100">
                    <div className="text-xs text-green-700 font-medium mb-1">Entrada</div>
                    <div className="text-2xl font-bold text-green-700">
                      {hasClockIn ? fmtTime((todayRecord as any).clockIn) : "—"}
                    </div>
                    {(todayRecord as any)?.expectedStart && (
                      <div className="text-xs text-muted-foreground mt-1">Previsto: {(todayRecord as any).expectedStart}</div>
                    )}
                  </div>
                  <div className="text-center p-4 rounded-lg bg-red-50 border border-red-100">
                    <div className="text-xs text-red-700 font-medium mb-1">Saída</div>
                    <div className="text-2xl font-bold text-red-700">
                      {hasClockOut ? fmtTime((todayRecord as any).clockOut) : "—"}
                    </div>
                    {(todayRecord as any)?.expectedEnd && (
                      <div className="text-xs text-muted-foreground mt-1">Previsto: {(todayRecord as any).expectedEnd}</div>
                    )}
                  </div>
                </div>

                {workedMinutes > 0 && (
                  <div className="text-center p-3 rounded-lg bg-muted/50">
                    <div className="text-xs text-muted-foreground">
                      {hasClockOut ? "Horas trabalhadas" : "Em serviço há"}
                    </div>
                    <div className="text-xl font-bold">{fmtMinutes(workedMinutes)}</div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Botões de ação */}
        <div className="space-y-3">
          {!hasClockIn && (
            <Button
              className="w-full h-14 text-lg gap-3"
              onClick={() => void registerClock("entrada")}
              disabled={clockInMut.isPending || isCollectingLocation}
            >
              <LogIn className="w-5 h-5" />
              {isCollectingLocation ? "Confirmando localização..." : clockInMut.isPending ? "Registrando..." : "Registrar Entrada"}
            </Button>
          )}

          {hasClockIn && !hasClockOut && (
            <Button
              variant="destructive"
              className="w-full h-14 text-lg gap-3"
              onClick={() => void registerClock("saída")}
              disabled={clockOutMut.isPending || isCollectingLocation}
            >
              <LogOut className="w-5 h-5" />
              {isCollectingLocation ? "Confirmando localização..." : clockOutMut.isPending ? "Registrando..." : "Registrar Saída"}
            </Button>
          )}

          {hasClockIn && hasClockOut && (
            <div className="flex items-center justify-center gap-2 p-4 rounded-lg bg-blue-50 text-blue-700">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Jornada de hoje encerrada!</span>
            </div>
          )}
        </div>

        {/* Aviso */}
        <div className="flex items-start gap-2 text-xs text-muted-foreground p-3 rounded-lg bg-muted/30">
          <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
          <span>O registro usa o horário oficial automático e não pode ser editado pelo funcionário. Ao bater o ponto, a localização é solicitada apenas naquele instante para auditoria. Em caso de erro, solicite ao administrador a correção manual.</span>
        </div>
      </div>
    </DashboardLayout>
  );
}
